/**
 * The 2D plan stage: hosts the SVG canvas + dimension-chip overlay, wires
 * pointer events into the gesture engine, and owns zoom and pan. The fitted
 * view (the whole room centered) is the minimum zoom; outside a drag the view
 * follows editor-core's reconcileViewport: when the room's boundary changes a
 * view at the fit follows it, while a zoomed-in view keeps its zoom, stays
 * inside the new fitted window, and pans to reveal what changed (the
 * selection). Zoomed in, a minimap shows in the corner.
 */

import { css } from '@emotion/react';
import {
  applyWheelToViewport,
  createWheelClassifier,
  type EditorMutableState,
  fitViewport,
  reconcileViewport,
  sameViewport,
  selectionBounds,
  useFloorPlanEditorState,
  type Viewport,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { handlePointerDown, handlePointerMove, handlePointerUp } from '../pointerLib';
import { DimsOverlay } from './DimsOverlay';
import { Minimap } from './Minimap';
import { PlanExportContext, PlanSvg } from './plan';

const STAGE_SVG_STYLE = css`
  label: STAGE_SVG_STYLE;
  position: absolute; inset: 0; width: 100%; height: 100%; display: block;
  touch-action: none;
`;

/** Read-only: handles stay clickable (selection) but never drag, so the
 *  resize/slide cursors would lie — neutralize them all in one rule. */
const READ_ONLY_CURSOR_STYLE = css`
  label: READ_ONLY_CURSOR_STYLE;
  [data-handle] { cursor: default !important; }
`;

const DIMS_OVERLAY_STYLE = css`
  label: DIMS_OVERLAY_STYLE;
  position: absolute; inset: 0; pointer-events: none;
  overflow: hidden; /* a chip never paints past the stage, over the panel beside it */
`;

/** The minimum-zoom viewport for the committed room on this stage. */
const fitFor = (st: EditorMutableState, stageW: number, stageH: number): Viewport => fitViewport(st.shape, stageW, stageH, st.viewport);

export interface PlanStageProps {
  stageW: number;
  stageH: number;
  visible: boolean;
}

export function PlanStage({ stageW, stageH, visible }: PlanStageProps): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const { st, mutate, stRef } = useFloorPlanEditorState();
  const fit = useMemo(() => fitFor(st, stageW, stageH), [st.shape, stageW, stageH]);
  const lastFitRef = useRef<Viewport | null>(null);

  useLayoutEffect(() => {
    if (!visible || st.drag) return;
    const previousFit = lastFitRef.current;
    lastFitRef.current = fit;
    const next = reconcileViewport(st.viewport, previousFit, fit, stageW, stageH, selectionBounds(st));
    if (!sameViewport(next, st.viewport)) {
      mutate(s => {
        s.viewport = next;
      });
    }
  }, [visible, fit, st.drag, stageW, stageH, st.viewport, st.selection, mutate]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    // A measurement editor (dim chip / panel field) may still hold focus. Its
    // commit fires on blur, but the browser dispatches blur AFTER pointerdown —
    // and if this click changes the selection, the editor unmounts uncommitted
    // (and chip/panel commits resolve through the selection). Blur it NOW:
    // focusout dispatches synchronously, so the commit lands — against the
    // still-current selection — before this click's own state change.
    const active = document.activeElement;
    if (active instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) {
      active.blur();
    }
    mutate(s => handlePointerDown(e.nativeEvent, s, svgEl, () => {}));
  }, [mutate]);

  // Window-level move/up so drags keep tracking outside the SVG bounds.
  useEffect(() => {
    if (!visible) return;
    const onMove = (e: PointerEvent) => {
      const svgEl = svgRef.current;
      if (!svgEl || !stRef.current?.drag) return;
      mutate(s => handlePointerMove(e, s, svgEl, () => {}));
    };
    const onUp = () => {
      if (!stRef.current?.drag) return;
      mutate(s => handlePointerUp(s, () => {}));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [visible, mutate, stRef]);

  // Wheel: pinch, mouse wheel or modifier zooms about the pointer; a trackpad
  // swipe pans (or zooms in from the fitted minimum). It is a native, non-passive listener because React
  // registers wheel as passive, so its handlers cannot stop the page scrolling.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || !visible) return;
    const classifyWheel = createWheelClassifier();
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svgEl.getBoundingClientRect();
      const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const wheel = {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        device: classifyWheel(e),
      };
      mutate(s => {
        if (s.drag) return;
        s.viewport = applyWheelToViewport(s.viewport, fitFor(s, stageW, stageH), wheel, anchor, stageW, stageH);
      });
    };
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, [visible, stageW, stageH, mutate]);

  return (
    <>
      <svg
        ref={svgRef}
        css={[STAGE_SVG_STYLE, st.readOnly && READ_ONLY_CURSOR_STYLE]}
        xmlns='http://www.w3.org/2000/svg'
        style={{ display: visible ? '' : 'none' }}
        onPointerDown={onPointerDown}
        data-testid='plan-stage'
      >
        {visible && <PlanSvg st={st} stageW={stageW} stageH={stageH} />}
      </svg>
      <div css={DIMS_OVERLAY_STYLE} style={{ display: visible ? '' : 'none' }}>
        {visible && <DimsOverlay stageW={stageW} stageH={stageH} />}
      </div>
      {visible && (
        <Minimap
          fit={fit}
          viewport={st.viewport}
          stageW={stageW}
          stageH={stageH}
          onViewport={viewport =>
            mutate(s => {
              s.viewport = viewport;
            })}
        >
          {(miniViewport, width, height) => (
            <PlanExportContext.Provider value={true}>
              <PlanSvg st={{ ...st, viewport: miniViewport }} stageW={width} stageH={height} showObjectLabels={false} />
            </PlanExportContext.Provider>
          )}
        </Minimap>
      )}
    </>
  );
}
